import React, { useEffect, useState } from "react";
import fileSizes from "../../utils/fileSizes.generated";

export const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const requests = new Map();
const cleanUrl = (href) => String(href || "").split(/[?#]/)[0];
const localSize = (href) => {
  const clean = cleanUrl(href);
  if (fileSizes.has(clean)) return fileSizes.get(clean);
  try {
    const url = new URL(clean, window.location.href);
    if (url.origin === window.location.origin) return fileSizes.get(decodeURIComponent(url.pathname));
  } catch { /* Invalid or absent URLs have no size metadata. */ }
  return undefined;
};

// HEAD retrieves metadata only; never download a whole PDF just to measure it.
const requestSize = (href) => {
  if (!requests.has(href)) {
    requests.set(href, fetch(href, { method: "HEAD" }).then(response => {
      const length = response.headers.get("content-length");
      if (!response.ok || !/application\/pdf/i.test(response.headers.get("content-type") || "") || !length || response.headers.get("content-encoding")) return null;
      const bytes = Number(length);
      return Number.isFinite(bytes) && bytes >= 0 ? bytes : null;
    }).catch(() => null));
  }
  return requests.get(href);
};

export default function FileSize({ href, bytes, fileName, className = "" }) {
  const known = Number.isFinite(bytes) ? bytes : localSize(href);
  const [remote, setRemote] = useState(null);
  const isPdf = /\.pdf$/i.test(cleanUrl(href)) || /\.pdf$/i.test(fileName || "") || known !== undefined;
  useEffect(() => {
    let active = true;
    if (href && isPdf && known === undefined) {
      requestSize(href).then(size => { if (active) setRemote({ href, size }); });
    }
    return () => { active = false; };
  }, [href, isPdf, known]);
  if (!href || !isPdf) return null;
  const size = formatFileSize(known ?? (remote?.href === href ? remote.size : null));
  // Keep the complete label in one text node and exclude it from translation
  // so Bhashini cannot duplicate fragments when the size or language changes.
  const label = `(${size || (remote?.href === href ? "size unavailable" : "checking size…")})`;
  return <span translate="no" className={`dont-translate bhashini-skip-translation bhashini-skip-mutation inline-block shrink-0 whitespace-nowrap text-xs font-normal leading-5 ${className}`} style={{ marginInlineStart: "0.35rem" }}>
    {label}
  </span>;
}
